import { type FC, useState } from "react";

import { type ApolloQueryResult } from "@apollo/client/core/types";
import { cx } from "@emotion/css";
import { Popover, PopoverPlacementTypes } from "@snapchat/snap-design-system";
import { ChevronDownIcon } from "@snapchat/snap-design-system-icons";

import { type MyLensesLens } from "../../../gql-generated/types";
import { type GetLensesListQuery } from "../../../lenses/api/queries";
import {
    PopoverContent,
    TextContainer,
    TextWrapper,
    chevronIcon,
    chevronIconFlipped,
    popoverStyle,
} from "../../styles";
import { Flexbox } from "../Flexbox.styles";

import { selectedLensIcon } from "./LensSearch.module.css";
import { LensSearchList } from "./LensSearchList";
import { AllLenses } from "./messages.generated";

interface LensSearchSelection {
    id: string;
    name: string;
    iconUrl: string;
    lensAccountId?: {
        lensSpaceId?: string | null;
    } | null;
}

interface LensSearchProps {
    lenses: readonly MyLensesLens[];
    selectedLens: LensSearchSelection | null;
    setSelectedLens: (lens: MyLensesLens | null) => void;
    searchText: string;
    setSearchText: (searchText: string) => void;
    hideAllLensesLink?: boolean;
    isFirstFetching?: boolean;
    isReFetching?: boolean;
    hasMore?: boolean;
    loadMore?: () => Promise<ApolloQueryResult<GetLensesListQuery>>;
    popoverClassName?: string;
    containerclassName?: string;
    lensFolderId?: string;
}

export const LensSearch: FC<LensSearchProps> = ({
    lenses,
    selectedLens,
    setSelectedLens,
    searchText,
    setSearchText,
    hideAllLensesLink,
    isFirstFetching,
    isReFetching,
    hasMore,
    loadMore,
    popoverClassName,
    containerclassName,
    lensFolderId,
}) => {
    const [visible, setVisible] = useState(false);

    const selectedLensBelongsToFolder =
        selectedLens?.id && (!lensFolderId || selectedLens.lensAccountId?.lensSpaceId === lensFolderId);

    return (
        <div className={containerclassName} data-testid="lens-search__container">
            <Popover
                trigger="click"
                placement={PopoverPlacementTypes.BOTTOM_LEFT}
                className={cx(popoverStyle, popoverClassName)}
                visible={visible}
                onVisibleChange={setVisible}
                content={
                    <LensSearchList
                        lenses={lenses}
                        selectedLens={selectedLens}
                        setSelectedLens={setSelectedLens}
                        searchText={searchText}
                        setSearchText={setSearchText}
                        hasMore={hasMore}
                        loadMore={loadMore}
                        isFirstFetching={isFirstFetching}
                        isReFetching={isReFetching}
                        hideAllLensesLink={hideAllLensesLink}
                    />
                }
                data-testid="lens-search"
            >
                <PopoverContent>
                    <TextContainer>
                        {selectedLensBelongsToFolder ? (
                            <Flexbox justifyContent="flex-start" alignItems="center">
                                <div
                                    className={selectedLensIcon}
                                    style={
                                        {
                                            "--icon-url": `url(${selectedLens.iconUrl})`,
                                        } as React.CSSProperties
                                    }
                                    data-testid="lens-search__selected-lens-icon"
                                />
                                <TextWrapper data-testid="lens-search__selected-lens">{selectedLens.name}</TextWrapper>
                            </Flexbox>
                        ) : (
                            <AllLenses />
                        )}
                    </TextContainer>
                    <ChevronDownIcon size={16} className={cx(chevronIcon, visible && chevronIconFlipped)} />
                </PopoverContent>
            </Popover>
        </div>
    );
};
